import { Component, createRef, memo } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import axios from "axios";

import ReactPlayer from "react-player";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FieldSubmitForm from "./FieldSubmitForm";
import FieldCanvas from "./FieldCanvas";
import Preloader from "@/components/Elements/Preloader";

import { FieldMarkingHintsConstant, FieldPoints, Stages } from "./constants";
import { markupApi } from "@/api";

import withRouter from "@/components/Elements/WithRouter";
import "../../scss/components/_field.scss";

const saveCalibrationToState = (camera) => (camera !== null ? camera : "");
const saveCalibrationToSubmit = (camera) => (camera !== "" ? +camera : null);

class Field extends Component {
  constructor(props) {
    super(props);

    this.state = {
      firstStageEditor: false,
      secondStageEditor: false,
      hint: "",
      field: {
        width: 0,
        height: 0,
        points: [],
        zone: [],
        updated: null,
      },
      updated: false,
      cameraModelList: null,
      cameraModelSelected: "",
      frame: null,
      showFrame: false,
      videoSource: null,
      frameError: null,
    };

    this.ref = createRef();
    this.canvas = createRef();
    this.reactPlayerRef = createRef();

    this.handleModeChange = this.handleModeChange.bind(this);
    this.handleSelectedPointChange = this.handleSelectedPointChange.bind(this);
    this.handleSelectedCamera = this.handleSelectedCamera.bind(this);
    this.secondStageButtonIsDisabled =
      this.secondStageButtonIsDisabled.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onUpdate = this.onUpdate.bind(this);
    this.handleShowFrame = this.handleShowFrame.bind(this);
  }

  componentDidMount() {
    axios
      .all([
        markupApi.getFieldMeta(this.props.match.params.videoId),
        markupApi.getCalibrationModels(),
      ])
      .then(
        axios.spread((fieldMeta, cameraModelList) => {
          const { field, source, calibration } = fieldMeta;
          this.setState((state) => ({
            ...state,
            field,
            // cameraModelList: cameraModelList.data,
            cameraModelSelected: saveCalibrationToState(calibration),
            videoSource: source,
          }));
        })
      );
  }

  componentDidUpdate() {
    if (!this.state.updated) {
      this.ref?.current?.onValuesUpdate({
        width: this.state.field?.width,
        height: this.state.field?.height,
      });
      this.canvas?.current?.resetPoints(this.state.field?.points);
      this.setState((state) => ({
        ...state,
        updated: true,
        hint: FieldMarkingHintsConstant.DEFAULT,
      }));
    }
  }

  handlePointsRemove() {
    if (this.canvas.current) {
      this.canvas.current.resetPoints(null);
    }
  }

  // Handle: editor mode for the iframe and canvas
  handleModeChange(stage) {
    this.setState((state) => ({
      ...state,
      [stage]: !state[stage],
    }));
    this.setState((state) => ({
      ...state,
      hint: state.firstStageEditor
        ? FieldMarkingHintsConstant.FIRST_STAGE
        : state.secondStageEditor
        ? FieldMarkingHintsConstant.SECOND_STAGE
        : FieldMarkingHintsConstant.DEFAULT,
    }));
  }

  secondStageButtonIsDisabled() {
    return (
      this.state.firstStageEditor ||
      this.state.field.fieldPoints?.length < 4 ||
      !this.state.field.height ||
      !this.state.field.width
    );
  }

  onChange(values) {
    this.setState((state) => ({
      ...state,
      field: {
        ...state.field,
        height: values.height,
        width: values.width,
      },
    }));
  }

  canSubmit() {
    const { width, height } = this.state.field;
    return width && height;
  }

  onSubmit = async () => {
    let points = this.canvas.current.state.points.map((point) => ({
      id: point.id,
      x: point.x,
      y: point.y,
    }));
    if (![4, 6, 9].includes(points.length)) {
      alert("The number of ooints on the field must be one of 9, 6 or 4!");
      return;
    }
    /* Field zone is deprecated and has value equal to field points. */
    const body = {
      width: this.state.field.width,
      height: this.state.field.height,
      points,
      zone: points,
      calibration: saveCalibrationToSubmit(this.state.cameraModelSelected),
    };

    try {
      await markupApi.updateFieldMeta(this.props.match.params.videoId, body);

      alert("The form is filled out correctly. Results saved");
    } catch (error) {
      alert(`The form has been filled out incorrectly. Error result: ${error}`);
    }
  };

  onUpdate() {
    this.forceUpdate();
  }

  handleSelectedPointChange(e) {
    if (this.canvas.current) {
      this.canvas.current.changeActive(e.target.value);
    }
  }

  handleSelectedCamera(e) {
    const currentCameraModel = e.target.value;

    this.setState((state) => ({
      ...state,
      cameraModelSelected: currentCameraModel,
      frameError: null,
    }));
  }

  handleShowFrame() {
    const currentTime = (
      this.reactPlayerRef?.current?.getCurrentTime() * 100
    ).toFixed(0);
    this.setState((state) => ({
      ...state,
      showFrame: !state.showFrame,
    }));
    if (this.state.showFrame) {
      this.setState((state) => ({
        ...state,
        pendingFrame: false,
      }));
    } else {
      const { videoId } = this.props.match.params;
      const { cameraModelSelected } = this.state;
      markupApi
        .getCalibrationFrame(videoId, cameraModelSelected, currentTime)
        .then((res) => {
          const data = Buffer.from(res.data, "binary").toString("base64");
          const frame = `data:${res.headers[
            "content-type"
          ].toLowerCase()};base64,${data}`;
          this.setState((state) => ({
            ...state,
            frame,
            pendingFrame: false,
            frameError: null,
          }));
        })
        .catch(() => {
          this.setState((state) => ({
            ...state,
            frameError: "Loading error",
            pendingFrame: false,
            showFrame: false,
          }));
        });
      this.setState((state) => ({
        ...state,
        pendingFrame: true,
      }));
    }
  }

  render() {
    const points =
      this.canvas.current &&
      this.state.firstStageEditor &&
      this.canvas.current.state.points.map((point) => (
        <option key={point.id} value={point.id}>
          {point.desc} [{point.x.toFixed(2)} : {point.y.toFixed(2)}]
        </option>
      ));

    return (
      <>
        {this.state.videoSource ? (
          <Container className="p-3" fluid>
            <Row>
              <Col>
                <div className="viewer-wrapper">
                  <div className="viewer ratio ratio-16x9">
                    <ReactPlayer
                      ref={this.reactPlayerRef}
                      width="100%"
                      height="100%"
                      controls={true}
                      url={this.state.videoSource}
                    />
                    {this.state.showFrame && !this.state.pendingFrame && (
                      <img id="frame" src={this.state.frame} alt="frame" />
                    )}
                    <FieldCanvas
                      ref={this.canvas}
                      state={this.state}
                      onUpdate={this.onUpdate}
                    />
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col className="d-flex flex-column">
                <Button
                  variant="outline-dark"
                  className="viewer__button my-2"
                  disabled={this.state.secondStageEditor}
                  onClick={() => this.handleModeChange(Stages.FIRST)}
                >
                  Field marking:{" "}
                  {this.state.firstStageEditor
                    ? "Редактирование ✏️"
                    : "Просмотр 🔍"}
                </Button>
                <FieldSubmitForm
                  ref={this.ref}
                  width={this.state.width}
                  height={this.state.height}
                  onChange={this.onChange}
                  onSubmit={this.onSubmit}
                />
                <Button
                  variant="outline-danger"
                  className="viewer__button"
                  onClick={() => this.handlePointsRemove(FieldPoints.FIELD)}
                >
                  Clear
                </Button>
              </Col>
              <Col className="d-flex flex-column">
                <Button
                  variant="outline-dark"
                  className="viewer__button my-2"
                  onClick={() => this.handleModeChange(Stages.SECOND)}
                  // disabled={this.secondStageButtonIsDisabled()}
                  disabled
                >
                  Area of responsibility:{" "}
                  {this.state.secondStageEditor
                    ? "Редактирование ✏️"
                    : "Просмотр 🔍"}
                </Button>
                <Button
                  variant="outline-danger"
                  className="viewer__button"
                  onClick={() => this.handlePointsRemove(FieldPoints.ZONE)}
                  disabled
                >
                  Clear
                </Button>
              </Col>
              <Col>
                <Button
                  type="submit"
                  variant="outline-primary"
                  className="form__form_button confirm-data my-2 w-100"
                  disabled={!this.canSubmit()}
                  onClick={() => this.ref.current.submit()}
                >
                  Send 🚀
                </Button>
                <select
                  value={this.canvas.current?.state.active || -1}
                  className="form-select my-2 w-100"
                  onChange={this.handleSelectedPointChange}
                  disabled={!this.state.firstStageEditor}
                >
                  <option value={-1} disabled hidden>
                    Select a point on the field
                  </option>
                  {points}
                </select>
                <div className="flex-column">
                  <div className="d-flex">
                    <Button
                      variant={this.state.showFrame ? "secondary" : "primary"}
                      disabled={this.state.cameraModelSelected === ""}
                      onClick={this.handleShowFrame}
                    >
                      {this.state.pendingFrame ? (
                        <FontAwesomeIcon spin icon={["fas", "spinner"]} />
                      ) : (
                        <FontAwesomeIcon
                          icon={[
                            "fas",
                            this.state.showFrame ? "eye-slash" : "eye",
                          ]}
                        />
                      )}
                    </Button>
                    {/*<select
                                            name="select-camera-model"
                                            value={this.state.cameraModelSelected}
                                            className="form-select w-100"
                                            onChange={this.handleSelectedCamera}
                                        >
                                            <option value="" label="Выберете модель камеры"/>
                                            {cameraModelList?.map(
                                                ({
                                                     id,
                                                     name
                                                 }) => (
                                                    <option
                                                        key={id}
                                                        value={id}
                                                        label={name}
                                                    />
                                                )
                                            )}
                                        </select>*/}
                  </div>
                  {this.state.frameError && (
                    <div className="alert alert-warning" role="alert">
                      {this.state.frameError}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Container>
        ) : (
          <Preloader />
        )}
      </>
    );
  }
}

export default withRouter(memo(Field));
